import { useReduxState } from 'hooks';
import { getSupervisorPermissions } from 'shared-helpers/users';
import { getAvailableUserPermissions } from 'lib/users';
import { Alert, Button, FlexContainer } from 'components/common';
import {
  ArrayField,
  Checkbox,
  FormGroup,
  FormSection,
  FormState,
  Select
} from 'components/common/form';
import { SupervisorSummaryEmailSettings } from 'components/supervisors';
import { SiteSettingsForm } from '.';

const SiteSettingsSupervisors = () => {
  const {
    messages,
    serverConfig: {
      automatedSupervisors,
      automatedSupervisorsAttributeIncludeLabelMatch,
      supervisorSummaryEmailEnabled,
      supervisorSummaryEmailFrequency,
      supervisorSummaryEmailOptions,
      userCustomAttributes,
      usersSuperadminLimit
    },
    serverConfig,
    theme
  } = useReduxState();

  const adminPermissions = getAvailableUserPermissions({
    messages,
    serverConfig
  });

  const supervisorPermissions = getSupervisorPermissions({ messages });

  const attributeKeyOptions: { value: string; label: string }[] = [];
  const attributeUserKeyOptions: typeof attributeKeyOptions = [];
  const attributeSupervisorKeyOptions: typeof attributeKeyOptions = [];
  userCustomAttributes.forEach((attr) => {
    if (
      attr.type === 'Boolean' &&
      (attr.permissions?.edit.admin.value ||
        attr.permissions?.edit.superadmin.value)
    ) {
      attributeKeyOptions.push({ value: attr.key, label: attr.title });
    }
    if (
      attr.permissions?.edit.admin.value ||
      attr.permissions?.edit.superadmin.value
    ) {
      attributeSupervisorKeyOptions.push({
        value: attr.key,
        label: attr.title
      });
    }
    attributeUserKeyOptions.push({ value: attr.key, label: attr.title });
  });

  const initialValues = {
    automatedSupervisors,
    automatedSupervisorsAttributeIncludeLabelMatch,
    supervisorSummaryEmailEnabled,
    supervisorSummaryEmailFrequency,
    supervisorSummaryEmailOptions
  };

  return (
    <SiteSettingsForm<typeof initialValues>
      initialValues={initialValues}
      padding={0}
      title={messages.adminSupervisors}
    >
      {(
        formState: FormState<{
          automatedSupervisors: typeof automatedSupervisors;
        }>
      ) => (
        <>
          <FormSection>
            {usersSuperadminLimit === 0 ? (
              <Alert
                text="Superadmin limit has been set to 0 which should mean that supervisors are not used"
                type="warning-light"
              />
            ) : null}

            <FormGroup label="Supervisors automation">
              <Checkbox
                field="automatedSupervisors.enabled"
                id="automated-supervisors"
                label="Create supervisors automatically"
              />
            </FormGroup>

            <FormGroup label="Supervisor automation attribute label match">
              <Checkbox
                disabled={!formState.values?.automatedSupervisors?.enabled}
                field="automatedSupervisorsAttributeIncludeLabelMatch"
                id="automated-supervisors-attribute-include-label-match"
                label="Check not just for attribute key match in automation, but also for label match"
                labelTooltip="This does not check for translations of labels"
              />
            </FormGroup>

            <FormGroup
              label="Supervisor attribute keys"
              labelHelpText="Boolean attribute keys defined for each user who is to be set as an admin during the integration. Only attributes that require superadmin editing privileges can be selected here."
              style={{ marginTop: 25 }}
            >
              <Select
                disabled={!formState.values?.automatedSupervisors?.enabled}
                field="automatedSupervisors.attributeKeys"
                id="automated-supervisors-attribute-keys"
                isMulti={true}
                placeholder="customAttributeKey"
                options={attributeKeyOptions}
                style={{ maxWidth: 400 }}
              />
            </FormGroup>

            <FormGroup label="Default admin permissions for created supervisors">
              <Select
                disabled={!formState.values?.automatedSupervisors?.enabled}
                isMulti={true}
                field="automatedSupervisors.adminPermissions"
                id="automated-supervisors-admin-permissions"
                placeholder="No admin permissions"
                options={Object.keys(adminPermissions).map((permission) => ({
                  label: adminPermissions[permission].label,
                  value: permission
                }))}
                style={{ maxWidth: 400 }}
              />
            </FormGroup>

            <FormGroup label="Default supervisor permissions for created supervisors">
              <Select
                disabled={!formState.values?.automatedSupervisors?.enabled}
                isMulti={true}
                field="automatedSupervisors.permissions"
                id="automated-supervisors-permissions"
                placeholder="No supervisor permissions"
                options={Object.keys(supervisorPermissions).map(
                  (permission) => ({
                    label: supervisorPermissions[permission],
                    value: permission
                  })
                )}
                style={{ maxWidth: 400 }}
              />
            </FormGroup>

            <FormGroup
              label="Attribute rules for supervisor groups"
              labelHelpText="Value of these attributes for a given user has to equal the chosen attribute on the supervisor."
            >
              <ArrayField field="automatedSupervisors.conditions">
                {({ add }) => (
                  <>
                    <ArrayField.Items>
                      {({ field, index, remove }) => (
                        <div>
                          {index !== 0 ? (
                            <div
                              style={{
                                color: theme.textColorLighter,
                                margin: '5px 0 5px 80px'
                              }}
                            >
                              OR
                            </div>
                          ) : null}

                          <FlexContainer style={{ flexGrow: 1 }}>
                            <Select
                              disabled={
                                !formState.values?.automatedSupervisors?.enabled
                              }
                              field={`${field}.userKey`}
                              placeholder="userKey"
                              options={attributeUserKeyOptions}
                              style={{ width: '100%', maxWidth: 200 }}
                            />

                            <div
                              style={{
                                color: theme.textColorLighter,
                                margin: '0 10px'
                              }}
                            >
                              EQ
                            </div>

                            <Select
                              disabled={
                                !formState.values?.automatedSupervisors?.enabled
                              }
                              field={`${field}.supervisorKey`}
                              placeholder="supervisorKey"
                              options={attributeSupervisorKeyOptions}
                              style={{ width: '100%', maxWidth: 200 }}
                            />

                            <Button
                              category="light"
                              disabled={
                                !formState.values?.automatedSupervisors?.enabled
                              }
                              icon="trash-alt"
                              onClick={remove}
                              style={{ marginLeft: 5 }}
                            />
                          </FlexContainer>
                        </div>
                      )}
                    </ArrayField.Items>

                    <Button
                      category="light"
                      disabled={
                        !formState.values?.automatedSupervisors?.enabled
                      }
                      icon="plus"
                      onClick={add}
                      size="small"
                      text="Add condition"
                      style={{ marginTop: 10 }}
                    />
                  </>
                )}
              </ArrayField>
            </FormGroup>
          </FormSection>

          <SupervisorSummaryEmailSettings isSiteSettingsView={true} />
        </>
      )}
    </SiteSettingsForm>
  );
};

export default SiteSettingsSupervisors;
